import { Metadata } from '@redwoodjs/web';
import { RiAddLine, RiShoppingCartLine, RiSubtractLine } from '@remixicon/react';
import { useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import Money from 'shared/utils/Money';
import Hint from 'src/components/atoms/Hint';
import { PageBody } from 'src/components/containers/PageBody';
import { PageHeader, PageType } from 'src/components/containers/PageHeader';
import { AgButton, AgText } from 'src/components/ui';
import { appLinks } from 'src/lib/routing';
import { ClearableSearch } from 'src/modules/grids';

import useCustomerPortalApi from '../useCustomerPortalApi';
import useCustomerPortalProducts from '../useCustomerPortalProducts';

export default function CustomerPortalProductsPage() {
  const products = useCustomerPortalProducts(-1);
  const navigate = useNavigate();
  const [cart, setCart] = useState<Record<number, number>>({});
  const { createPurchaseOrder } = useCustomerPortalApi();
  const [search, setSearch] = useState('');

  const changeQuantity = (productId: number, quantity: number) => {
    if (quantity === 0) {
      const newCart = { ...cart };
      delete newCart[productId];
      setCart(newCart);
    } else {
      setCart({ ...cart, [productId]: quantity });
    }
  };

  const createOrder = async () => {
    const slug = await createPurchaseOrder({
      lineItems: Object.entries(cart).map(([id, unitsOrdered]) => ({ productId: +id, unitsOrdered })),
    });
    await navigate(appLinks.editCustomerPortalPurchaseOrder({ slug }));
  };

  const total = Object.values(cart).reduce((acc, quantity) => acc + quantity, 0);

  const filteredProducts = useMemo(
    () =>
      products.filter(
        (product) =>
          product.label?.toLowerCase().includes(search.toLowerCase()) ||
          product.code?.toLowerCase().includes(search.toLowerCase())
      ),
    [products, search]
  );

  const emptyCart = Object.keys(cart).length === 0;

  return (
    <>
      <Metadata title="Products" />
      <PageHeader pageType={PageType.CustomerPortalProducts}>
        <div className="ml-auto flex my-auto gap-1">
          <Hint title={emptyCart ? 'Add products to your cart before creating an order' : ''}>
            <div>
              <AgButton variant="white" isDisabled={emptyCart} onPress={createOrder}>
                <RiShoppingCartLine size={20} />
                Go to Purchase Order ({total})
              </AgButton>
            </div>
          </Hint>
        </div>
      </PageHeader>
      <PageBody sx={{ flexDirection: 'column', gap: 3 }}>
        <div className="flex flex-col gap-4">
          <div className="max-w-md">
            <ClearableSearch searchState={search} onSearch={setSearch} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 min-w-[75%]">
            {filteredProducts.map((product) => (
              <div
                key={product.id}
                className="flex flex-col gap-1 h-full rounded-md border border-neutral-5 rounded bg-white p-4"
              >
                <AgText size="16">{product.label}</AgText>
                <AgText size="14" className="text-neutral-8 -mb-1">
                  {product.unit}
                </AgText>
                <AgText size="14" className="text-neutral-8">
                  {product.code}
                </AgText>
                <div className="grow" />
                <AgText size="16" className="font-bold">
                  {Money.toHuman(product.price)}
                </AgText>
                {cart[product.id] ? (
                  <QuantityControl
                    quantity={cart[product.id]}
                    confirm={(quantity) => changeQuantity(product.id, quantity)}
                  />
                ) : (
                  <AgButton variant="primary" onPress={() => changeQuantity(product.id, 1)}>
                    <RiAddLine size={20} />
                    Add to Order
                  </AgButton>
                )}
              </div>
            ))}
          </div>
        </div>
      </PageBody>
    </>
  );
}

function QuantityControl({ quantity, confirm }) {
  const [value, setValue] = useState(quantity + '');

  const onClickButton = (add = false) => {
    const v = parseInt(value) || 0;
    const newValue = v + (add ? 1 : -1);
    setValue(String(newValue));
    confirm(newValue);
  };

  return (
    <div className="flex flex-row gap-2 items-center">
      <AgButton variant="ghost" size="icon-sm" shape="circle" onPress={() => onClickButton(false)}>
        <RiSubtractLine size={20} />
      </AgButton>
      <input
        type="number"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={() => confirm(parseInt(value) || 0)}
        className="w-full border border-neutral-5 rounded text-center h-8 text-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
      />
      <AgButton variant="ghost" size="icon-sm" shape="circle" onPress={() => onClickButton(true)}>
        <RiAddLine size={20} />
      </AgButton>
    </div>
  );
}
